// import components
import Box from '@mui/material/Box';
import { Dialog } from 'components/shared';
import { DataGrid } from '../DataGrid';
import { LoadingError } from '../LoadingError/LoadingError';
import { Drawer } from '../index';
import { CustomGridTreeDataGroupingCell } from './components/CustomGridTreeDataGroupingCell';

// import hooks
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import props
import { DialogProps } from '@mui/material/Dialog';
import {
  DataGridProProps,
  GridRowId,
  GridRowParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import {
  CheckBoxSelectionProps,
  CustomDataGridProps,
  DataGridAction,
  DataGridActionsFn
} from '../DataGrid/DataGrid.types';
import { CustomDrawerProps } from '../Drawer/Drawer';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';

// import utils
import { TFunction } from 'i18next';
import { defaultTo, isNil } from 'ramda';
import { StyledDrawerBox } from './TableCRUDLayout.styles';

const PREFIX = 'TableCRUDLayout';

const classes = {
  createButtonContainer: `${PREFIX}-createButtonContainer`,
  dataGridContainer: `${PREFIX}-dataGridContainer`
};

export interface FormComponentProps<T = object> {
  rowId: number | string | undefined;
  rowData?: T | undefined;
  rows?: T[];
  handleClose: (props?: any) => void;
}

export type TableCRUDLayoutProps<T extends GridValidRowModel = any> = Omit<
  CustomDataGridProps<T>,
  'rows'
> & {
  FormComponent?: React.FC<FormComponentProps<T> & any>;
  formContainer?: 'drawer' | 'dialog';
  renderFormContainer?: boolean;
  handleDelete?: (ids: GridRowId[], rows?: T[]) => void;
  drawerProps?: Partial<CustomDrawerProps>;
  drawerWidth?: number;
  rows?: DataGridProProps<T>['rows'];
  dialogProps?: Partial<DialogProps>;
  onLoadingError?: (props?: any) => unknown;
  hasLoadingError?: boolean | undefined;
  hideCreateAction?: boolean;
  onClose?: (props?: any) => any;
  editable?: boolean;
  customEditTitle?: string;
  customCreateTitle?: string;
  additionalFormProps?: Record<string, any>;
  onAddClick?: (rowSetter: () => void) => void;
  collapse?: number;
  hideDescendantCount?: boolean;
  isContentRangePagination?: boolean;
  page?: number;
  pageSize?: number;
  rowCount?: number;
  onPaginationModelChange?: (paginationModel: {
    page: number;
    pageSize: number;
  }) => void;
  initialState?: GridInitialStatePro | undefined;
  tableBgColor?: string;
  dynamicHeight?: boolean;
  isCellEditable?: DataGridProProps<T>['isCellEditable'];
  isRowSelectable?: DataGridProProps<T>['isRowSelectable'];
  getExtraRowClassName?: (params: GridRowParams<T>) => string;
  isRowSelectableFromParent?: (param) => boolean;
  disableRowSelectionOnClick?: boolean;
  hasTreeData?: boolean;
  customHeight?: string;
};

export interface TableCRUDLayoutHandlers<T extends GridValidRowModel = any> {
  /**
   * Closes the container (drawer/dialog)
   */
  handleClose: () => void;
  /**
   * Opens the container (drawer/dialog)
   */
  handleOpen: () => void;
  /**
   * Opens the container (drawer/dialog) and sets the state of id and rowData
   */
  handleEditClick?: (id: number | string, rowData: T) => void;
}

const createTitle = (
  rowId: number | string | undefined,
  t: TFunction,
  customEditTitle?: string,
  customCreateTitle?: string
): string => {
  if (isNil(rowId)) {
    return customCreateTitle ?? t('new');
  }

  return customEditTitle ?? t('edit');
};

/**
 * This component is a wrapper for the commonly used table CRUD layout.
 * It renders the shared DataGrid, the shared Drawer or Dialog for form handling and also handles the create/edit part.
 */
const TableCRUDLayout = <T extends GridValidRowModel = any>({
  FormComponent,
  additionalFormProps,
  rows,
  columns,
  collapse,
  drawerProps,
  drawerWidth,
  dialogProps,
  hasLoadingError,
  onLoadingError,
  hideCreateAction,
  canBeEditedProp,
  canBeDeletedProp,
  onClose,
  editable = true,
  customActions,
  customEditTitle,
  customCreateTitle,
  formContainer = 'drawer',
  renderFormContainer = true,
  dynamicHeight,
  onAddClick,
  shouldRenderToolBar = true,
  hideDescendantCount,
  disableActions,
  isContentRangePagination,
  page = 0,
  pageSize = 50,
  pageSizeOptions = [50],
  rowCount = 0,
  paginationMode = 'client',
  initialState,
  onPaginationModelChange,
  tableBgColor,
  groupingColDef,
  isCellEditable = () => false,
  isRowSelectable = () => false,
  getExtraRowClassName,
  isRowSelectableFromParent,
  disableRowSelectionOnClick,
  hasTreeData,
  apiRef,
  customHeight = 'calc(100% - 50px)',
  autosizeOnMount,
  autosizeOptions,
  ...props
}: TableCRUDLayoutProps<T> & CheckBoxSelectionProps) => {
  const [containerOpen, setContainerOpen] = useState(false);
  const [rowId, setRowId] = useState(undefined);
  const [rowData, setRowData] = useState(undefined);
  const [_page, setPage] = useState(page); // For client-side pagination
  const [_pageSize, setPageSize] = useState(pageSize);
  const { t } = useTranslation();

  const handleClose = () => {
    setContainerOpen(false);

    onClose && onClose(rowData);
  };

  const onExited = () => {
    setRowId(undefined);
    setRowData(undefined);
  };

  const handleEditClick = (id, rowData) => {
    setRowId(id);
    setRowData(rowData);
    setContainerOpen(true);
  };

  const handleOpen = () => setContainerOpen(true);

  const handleAddClick = () => {
    onAddClick && onAddClick();
    handleOpen();
  };

  const handlePageChange = ({ page, pageSize }) => {
    setPage(page);
    setPageSize(pageSize);
  };

  const getCustomActions = useCallback<DataGridActionsFn<T>>(
    (id: string | number, row?: T): DataGridAction<T>[] => {
      const actions =
        typeof customActions === 'function'
          ? customActions(id, row)
          : customActions ?? [];

      return actions.map((customAction) => ({
        ...customAction,
        onClick: (id, row, event) => {
          // pass the handlers to the action
          customAction.onClick &&
            customAction.onClick(
              id,
              row,
              {
                handleOpen,
                handleEditClick,
                handleClose
              } as TableCRUDLayoutHandlers<T>,
              event
            );
        }
      }));
    },
    [customActions]
  );

  const defaultGroupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: t('name'),
    renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
    minWidth: 300,
    hideDescendantCount: hideDescendantCount
  };

  return (
    <>
      {hasLoadingError ? (
        <LoadingError {...(onLoadingError && { reFetch: onLoadingError })} />
      ) : (
        <>
          {formContainer === 'drawer' && renderFormContainer && (
            <Drawer
              {...drawerProps}
              open={containerOpen}
              onClose={handleClose}
              title={createTitle(rowId, t, customEditTitle, customCreateTitle)}
              SlideProps={{ onExited: onExited }}
            >
              <StyledDrawerBox drawerWidth={drawerWidth}>
                {FormComponent && (
                  <FormComponent
                    rowId={rowId}
                    rowData={rowData}
                    rows={rows}
                    handleClose={handleClose}
                    isDrawerOpen={containerOpen}
                    {...additionalFormProps}
                  />
                )}
              </StyledDrawerBox>
            </Drawer>
          )}
          {formContainer === 'dialog' && renderFormContainer && (
            <Dialog
              open={containerOpen}
              title={createTitle(rowId, t, customEditTitle, customCreateTitle)}
              handleClose={handleClose}
              TransitionProps={{ onExited: onExited }}
              {...dialogProps}
            >
              {FormComponent && (
                <FormComponent
                  rowId={rowId}
                  rowData={rowData}
                  handleClose={handleClose}
                  {...additionalFormProps}
                />
              )}
            </Dialog>
          )}
          <Box className={classes.dataGridContainer} height={customHeight}>
            <DataGrid<T>
              {...props}
              apiRef={apiRef}
              shouldRenderToolBar={shouldRenderToolBar}
              rows={defaultTo([], rows)}
              columns={columns}
              pagination
              {...(isContentRangePagination
                ? {
                    onPaginationModelChange,
                    paginationMode: 'server',
                    page,
                    pageSize,
                    rowCount
                  }
                : {
                    onPaginationModelChange: handlePageChange,
                    paginationMode,
                    page: _page,
                    pageSize: _pageSize
                  })}
              groupingColDef={groupingColDef ?? defaultGroupingColDef}
              initialState={initialState}
              disableActions={disableActions}
              pageSizeOptions={
                isContentRangePagination ? [25, 50, 100] : pageSizeOptions
              }
              defaultGroupingExpansionDepth={collapse}
              {...(editable && {
                onEditClick: (id, row) => handleEditClick(id, row)
              })}
              isCellEditable={isCellEditable}
              isRowSelectable={isRowSelectable}
              canBeEditedProp={canBeEditedProp}
              getExtraRowClassName={getExtraRowClassName}
              isRowSelectableFromParent={isRowSelectableFromParent}
              disableRowSelectionOnClick={disableRowSelectionOnClick}
              canBeDeletedProp={canBeDeletedProp}
              dynamicHeight={dynamicHeight}
              headerContent={props.headerContent}
              tableBgColor={tableBgColor}
              autosizeOnMount={autosizeOnMount}
              autosizeOptions={autosizeOptions}
              hasTreeData={hasTreeData}
              {...(!hideCreateAction ? { onAddClick: handleAddClick } : {})}
              {...(customActions
                ? {
                    customActions: getCustomActions
                  }
                : '')}
            />
          </Box>
        </>
      )}
    </>
  );
};

TableCRUDLayout.displayName = 'TableCRUDLayout';

export default TableCRUDLayout;
